import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { PageSection, CheckGrid, NumberSteps, CTABand } from "../components/site/ContentBlocks";
import { serviceLd } from "../components/site/SEO";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "../components/site/Reveal";
import { VN_AGENT_SERVICES, VN_PROCESS, PRODUCT_PAGES } from "../data/content";

export default function VietnamSourcing() {
    const vnProducts = PRODUCT_PAGES.filter((p) => p.id !== "oil-gas");
    return (
        <div className="grain bg-navy-deep" data-testid="vietnam-sourcing-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "Vietnam Sourcing Agent | Product Sourcing Company Vietnam — Viet Medica Ltd",
                        description:
                            "Source products from Vietnam with local agents who support supplier searches, samples, documentation and shipment planning for international buyers.",
                        path: "/vietnam-sourcing-agent",
                        jsonLd: serviceLd(
                            "Vietnam Product Sourcing and Export Support",
                            "Local agent support in Vietnam for international buyers — supplier searches, samples, documentation and export coordination.",
                            "/vietnam-sourcing-agent"
                        ),
                    }}
                    crumbs={[{ label: "International Trading", href: "/international-trading" }, { label: "Vietnam Sourcing" }]}
                    eyebrow="Trading and Sourcing from Vietnam"
                    title="Your Local Connection for"
                    titleEm="Products from Vietnam."
                    copy="Looking to source products or develop trading relationships in Vietnam? Viet Medica Ltd has local agents and business connections in Vietnam who can help international buyers identify suitable suppliers, clarify product requirements and coordinate important stages of the sourcing and export process. Our local presence helps bridge language, communication and market differences, giving overseas buyers a practical point of contact inside Vietnam."
                />

                <PageSection eyebrow="On the Ground in Vietnam" title="What Our Vietnam Agents Can Support" testid="vn-agent-services">
                    <CheckGrid items={VN_AGENT_SERVICES} cols={3} />
                    <FadeUp>
                        <p className="mt-10 max-w-3xl text-sm leading-relaxed text-slate-400">
                            Where regulated inspections, customs clearance or legal verification are required, we
                            can coordinate with relevant independent professional service providers. Viet Medica
                            Ltd does not itself perform regulated inspections, customs clearance or legal
                            verification.
                        </p>
                    </FadeUp>
                </PageSection>

                <PageSection light eyebrow="The Process" title="Vietnam Sourcing Process" testid="vn-process">
                    <NumberSteps steps={VN_PROCESS} light />
                </PageSection>

                <PageSection eyebrow="Products" title="Products from Vietnam" testid="vn-products">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        {vnProducts.map((p, i) => (
                            <FadeUp key={p.id} delay={i * 0.08} className="h-full">
                                <Link
                                    to={p.path}
                                    data-testid={`vn-product-${p.id}`}
                                    className="group relative flex h-[280px] flex-col justify-end overflow-hidden border border-white/8"
                                >
                                    <img
                                        src={p.image}
                                        alt={p.imageAlt}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-[1.06]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/40 to-transparent" aria-hidden="true" />
                                    <div className="relative flex items-end justify-between p-6">
                                        <h3 className="font-serif text-2xl font-light text-ivory">{p.title}</h3>
                                        <ArrowUpRight size={16} className="text-gold transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
                                    </div>
                                </Link>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp delay={0.1}>
                        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-slate-400">
                            Looking for a product not shown here? Tell our Vietnam team what you need. We can
                            review the requirement and determine whether our local network may be able to assist.
                        </p>
                    </FadeUp>
                </PageSection>

                <section className="bg-navy-surface py-20 lg:py-28">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <CTABand
                            title="Tell our Vietnam team what you need."
                            primary={{ label: "Send Product Requirement", to: "/vietnam-sourcing-enquiry", testid: "vn-sourcing-enquiry-cta" }}
                            secondary={{ label: "Find Suppliers in Vietnam", to: "/find-suppliers-in-vietnam", testid: "vn-find-suppliers-cta" }}
                        />
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
